import React, { useEffect, useMemo, useState } from "react";
import { EditOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import type { GlossaryEntry } from "../../../glossary";
import { GLOSSARY } from "../../../glossary";
import { saveGlossaryEntry } from "../../../redux/coilConfigurator/actions";
import type { RootState } from "../../../redux/appState";
import GlossaryEditModal from "./GlossaryEditModal";
import CcButton from "./CcButton";
import "./glossary-edit-modal.css";

interface GlossaryDrawerProps {
  open: boolean;
  onClose: () => void;
}

interface GlossaryListItem {
  entryKey: string;
  entry: GlossaryEntry;
}

const GlossaryDrawer = ({ open, onClose }: GlossaryDrawerProps) => {
  const dispatch = useDispatch();
  const glossaryEntries = useSelector((state: RootState) => state.coilConfigurator.glossary.entries);
  const glossaryLoaded = useSelector((state: RootState) => state.coilConfigurator.glossary.loaded);
  const [query, setQuery] = useState("");
  const [editingItem, setEditingItem] = useState<GlossaryListItem | null>(null);

  const entries = glossaryLoaded && Object.keys(glossaryEntries).length > 0
    ? glossaryEntries
    : GLOSSARY;

  useEffect(() => {
    if (!open) {
      setQuery("");
      setEditingItem(null);
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !editingItem) onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose, editingItem]);

  const terms = useMemo(() => {
    const list: GlossaryListItem[] = Object.entries(entries).map(([entryKey, entry]) => ({
      entryKey,
      entry,
    }));
    const normalized = query.trim().toLowerCase();
    const filtered =
      normalized === ""
        ? list
        : list.filter(
            ({ entry }) =>
              entry.title.toLowerCase().includes(normalized) ||
              entry.definition.toLowerCase().includes(normalized),
          );

    return filtered.slice().sort((a, b) => a.entry.title.localeCompare(b.entry.title));
  }, [entries, query]);

  const handleSave = (entry: GlossaryEntry) => {
    if (!editingItem) return;
    dispatch(saveGlossaryEntry(editingItem.entryKey, entry));
    setEditingItem(null);
  };

  if (!open) return null;

  return (
    <>
      <div className="glossary-drawer is-open" aria-hidden="false">
        <button
          type="button"
          className="glossary-drawer__backdrop"
          aria-label="Close glossary"
          onClick={() => {
            if (!editingItem) onClose();
          }}
        />
        <aside
          className="glossary-drawer__panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby="glossary-drawer-title"
        >
          <div className="glossary-drawer__header">
            <h2 className="glossary-drawer__title" id="glossary-drawer-title">
              Glossary
            </h2>
            <CcButton variant="ghost" onClick={onClose}>
              Close
            </CcButton>
          </div>

          <div className="glossary-drawer__search">
            <input
              type="search"
              id="glossary-search"
              placeholder="Search terms..."
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              autoComplete="off"
            />
          </div>

          <ul className="glossary-drawer__list" aria-live="polite" aria-label="Glossary terms">
            {terms.length === 0 ? (
              <li className="glossary-drawer__empty">No terms match your search.</li>
            ) : (
              terms.map(({ entryKey, entry }) => (
                <li key={entryKey} className="glossary-drawer__item">
                  <div className="glossary-drawer__term-row">
                    <p className="glossary-drawer__term">{entry.title}</p>
                    <CcButton
                      variant="icon"
                      className="glossary-drawer__edit-button"
                      aria-label={`Edit ${entry.title}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        setEditingItem({ entryKey, entry });
                      }}
                    >
                      <EditOutlined />
                    </CcButton>
                  </div>
                  <p className="glossary-drawer__definition">{entry.definition}</p>
                </li>
              ))
            )}
          </ul>
        </aside>
      </div>

      <GlossaryEditModal
        open={editingItem != null}
        initialEntry={editingItem?.entry ?? { title: "", definition: "" }}
        onClose={() => setEditingItem(null)}
        onSave={handleSave}
      />
    </>
  );
};

export default GlossaryDrawer;
